import { useContext } from "react";
import { FiPlusSquare, FiBook, FiLogOut, FiBarChart, FiSettings, FiKey, FiSearch, FiHome, FiGitPullRequest, FiPieChart, FiFastForward, FiTablet, FiBarChart2, FiUsers, FiClipboard, FiClock } from 'react-icons/fi';
import { FaCar, FaBus } from "react-icons/fa";
import { Close, QrCodeScannerOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { SessionContext } from "../../utility/useSession";
import usePaths from "../../utility/paths";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";
import { isAdmin, hasKeyMasterPermissions, isSysAdmin } from "../../utility/utils";
import { LaneAllocationSystem } from "../../logic/api";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const style = {
  fontSize: 24,
  padding: '16px 0',
};

interface IMenuItem {
  name: string;
  icon: JSX.Element;
  link: string;
  formatMessage: string;
}

export default () => {
  const paths = usePaths();
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();

  const isVehicleMoveUiReleased = useReleaseManagement(ReleaseFlag.vehicleMoveUi);
  const isKeyMasterRoleReleased = useReleaseManagement(ReleaseFlag.releaseKeyMasterRole);
  const isSydneyDualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isDriverShiftTrackerReleased = useReleaseManagement(ReleaseFlag.releaseDriverShiftTracker);
  const isScanningPageReleased = useReleaseManagement(ReleaseFlag.releaseScanningPage);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isRbacLanePlanningReleased = useReleaseManagement(ReleaseFlag.releaseRbacLanePlanningPermissions);

  if (session.current.site == null) {
    return [];
  }

  const prefs = session.current.site.preferences;

  const closeMenu: IMenuItem = {
    name: 'Close',
    icon: <Close style={style} />,
    link: paths.close,
    formatMessage: t("menuItems.close"),
  };

  const search: IMenuItem = {
    name: 'Search',
    icon: <FiSearch style={style} />,
    link: paths.search,
    formatMessage: t("menuItems.search"),
  };

  const bookings: IMenuItem = {
    name: 'Bookings',
    icon: <FiBook style={style} />,
    link: paths.bookings,
    formatMessage: t("menuItems.bookings"),
  };

  const newBooking: IMenuItem = {
    name: 'New Booking',
    icon: <FiPlusSquare style={style} />,
    link: paths.bookingCreate,
    formatMessage: t("menuItems.createBooking"),
  };

  const expressCheckOut: IMenuItem = {
    name: 'Express CheckOut',
    icon: <FiFastForward style={style} />,
    link: paths.expressCheckOut,
    formatMessage: t("menuItems.expressCheckout"),
  };

  const locations: IMenuItem = {
    name: 'Locations',
    icon: <FiHome style={style} />,
    link: paths.locations,
    formatMessage: t("menuItems.locations"),
  };

  let reports: IMenuItem | null = null;
  if (session.current.site.preferences.reports_v1 == true || isSysAdmin(session) == true) {
    reports = {
      name: 'Reports',
      icon: <FiBarChart style={style} />,
      link: paths.reports,
      formatMessage: t("menuItems.reports"),
    };
  }

  let reportsExPDForms: IMenuItem | null = null;
  if (session.current.site.preferences.reports_v2 == true || isSysAdmin(session) == true) {
    reportsExPDForms = {
      name: 'Reports V2',
      icon: <FiBarChart2 style={style} />,
      link: paths.reportsV2,
      formatMessage: "Reports V2",
    };
  }

  let insights: IMenuItem | null = null;
  if (prefs.overview_enabled || isSysAdmin(session)) {
    insights = {
      name: 'Insights',
      icon: <FiPieChart style={style} />,
      link: paths.insights("overview"),
      formatMessage: t("menuItems.insights"),
    };
  }

  const kmos: IMenuItem = {
    name: 'KMOS',
    icon: <FiGitPullRequest style={style} />,
    link: paths.kmos,
    formatMessage: t("menuItems.kmos"),
  };

  const vehicleManager: IMenuItem = {
    name: 'Vehicles',
    icon: <FaCar style={style} />,
    link: paths.vehicleManager,
    formatMessage: t("menuItems.vehicleManager"),
  };

  const keyManager: IMenuItem = {
    name: 'Keys',
    icon: <FiKey style={style} />,
    link: paths.keyManager,
    formatMessage: t("menuItems.keyManager"),
  };

  const settings: IMenuItem = {
    name: 'Settings',
    icon: <FiSettings style={style} />,
    link: paths.settings,
    formatMessage: t("menuItems.settings"),
  };

  const logOut: IMenuItem = {
    name: 'Log Out',
    icon: <FiLogOut style={style} />,
    link: paths.logout,
    formatMessage: t("menuItems.logout"),
  };

  const devices: IMenuItem = {
    name: 'Device Manager',
    icon: <FiTablet style={style} />,
    link: paths.devices,
    formatMessage: t("menuItems.devices"),
  };

  const busWalkArounds: IMenuItem = {
    name: 'Bus Walkarounds',
    icon: <FaBus style={style} />,
    link: paths.busWalkarounds,
    formatMessage: t("menuItems.busWalkarounds"),
  };

  const driverShiftTracker: IMenuItem = {
    name: 'Driver Availability',
    icon: <FiClipboard style={style} />,
    link: paths.driverShiftTracker,
    formatMessage: t("menuItems.driverShiftTracker"),
  };

  const scanning: IMenuItem = {
    name: 'Scanning',
    icon: <QrCodeScannerOutlined style={style} />,
    link: paths.scanning,
    formatMessage: t("menuItems.scanning"),
  };

  const menuItems: IMenuItem[] = [];

  menuItems.push(closeMenu);

  if (hasPermission("bookingManagement.booking.read")) {
    menuItems.push(search);
    menuItems.push(bookings);
  }

  if (prefs.booking_create_enabled == true && hasPermission("bookingManagement.booking.create")) {
    menuItems.push(newBooking);
  }
  if (prefs.assetlocations_enabled == true) {
    menuItems.push(locations);
  }

  if (prefs.express_checkout_enabled == true) {
    menuItems.push(expressCheckOut);
  }

  if (isVehicleMoveUiReleased) {
    menuItems.push(vehicleManager);
  }

  if (prefs.keymovements_enabled == true) {
    menuItems.push(keyManager);
  }

  const canSeeDriverShiftTracker = isDriverShiftTrackerReleased && prefs.driver_shift_tracker_enabled === true && (
    isRbacReleased
      ? hasPermission("shiftTracking.shift.view")
      : hasKeyMasterPermissions(session)
  );

  if (canSeeDriverShiftTracker) {
    menuItems.push(driverShiftTracker);
  }

  const canSeeScanningPage = isScanningPageReleased && prefs.scanning_page_enabled === true;
  if (canSeeScanningPage) {
    menuItems.push(scanning);
  }

  const canAccessKmos = isRbacLanePlanningReleased
    ? hasPermission("lanePlanning.planning.view")
    : !isKeyMasterRoleReleased || hasKeyMasterPermissions(session);

  if (isSydneyDualParkAllocationReleased) {
    if (
      [LaneAllocationSystem.DAS, LaneAllocationSystem.KMOS].includes(prefs.lane_allocation_system) &&
      canAccessKmos
    ) {
      menuItems.push(kmos);
    }
  } else {
    if (prefs.kmos_enabled == true && canAccessKmos) {
      menuItems.push(kmos);
    }
  }

  if (reports != null) {
    menuItems.push(reports);
  }

  if (reportsExPDForms != null) {
    menuItems.push(reportsExPDForms);
  }

  if (hasPermission("web.insights.read")) {
    if (insights !== null) {
      menuItems.push(insights);
    }
  }

  if (isAdmin(session) && prefs.enable_device_manager == true) {
    menuItems.push(devices);
  }

  if (prefs.bus_walkaround_enabled == true) {
    menuItems.push(busWalkArounds);
  }

  menuItems.push(settings);
  menuItems.push(logOut);

  return menuItems;
};
